import { Fragment } from 'react';
import { Link } from 'react-router-dom';

import { useMutation } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';

import { Avatar } from '@components/avatar';
import { IconButton } from '@components/icon-button';
import { IconAssign } from '@components/icons/assign';
import { IconEdit } from '@components/icons/edit';
import { IconOnboarding } from '@components/icons/onboarding';
import { IconPersonRemove } from '@components/icons/person-remove';
import { Menu } from '@components/menu';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';
import { Tooltip } from '@components/tooltip';
import { useIsMobile } from '@hooks/use-is-mobile';
import { abbreviateDate } from '@utils/date';

import { api } from 'api';
import { applicantStatusTranslationMap } from 'locale';

import { ApplicationMetaRow } from 'components/application-meta-row';
import { ApplicationLabels } from 'components/ApplicationLabels';
import { ApplicationOptions } from 'components/ApplicationOptions';
import { Box } from 'components/Box';
import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';
import { getApplicationStatus, getAvatarColor, getStatusDetail } from 'helpers/application-status';
import { useStageEntryJourneyPrompt } from 'integrations/journeys/job-triggers/use-stage-entry-journey';
import { useStore } from 'models/Provider';

import { StageMenu } from './StageMenu';

export const ApplicationHeader = observer(({ application }) => {
  const { lang } = useStore();
  const { t } = useStore();
  const { job, picture, name } = application;
  const isMobile = useIsMobile();
  const { showError, showSuccess } = useToaster();
  const { hasRolePermission } = usePermissions();
  const { showModal } = useModal();
  const { promptOrChainStageEntryJourney } = useStageEntryJourneyPrompt(job.id);
  const dt = abbreviateDate(application.date, lang);
  const status = getApplicationStatus(application);
  const statusDetail = getStatusDetail({ application, status, lang, t });

  async function changeStage(stageId) {
    const previousStageId = application.jobStageId;
    await application.changeStage(stageId, showModal);
    // changeStage no-ops when the stage is unchanged and reverts jobStageId on
    // failure — only a real, persisted move should prompt for the journey.
    if (previousStageId !== stageId && application.jobStageId === stageId) {
      promptOrChainStageEntryJourney([application.id], stageId, {
        job,
        isApplicationHired: application.isHired,
      });
    }
  }

  function assign() {
    showModal('ASSIGN_APPLICANT', {
      applicationId: application.id,
      applicationName: application.name,
      applicationAssignedToId: application.assignedTo?.id,
    });
  }

  const removeAssignee = useMutation(() => api.applications.setAssignedTo(application.id, null), {
    onSuccess: () => {
      // Update store state for those not using react-query
      application.setAssignedTo(null);
      showSuccess(t('application.setassignedto.remove', application.name));
    },
    onError: () => {
      showError(
        t('application.setassignedto.error', application.name, application.assignedTo.name)
      );
    },
  });

  return (
    <Box flex flexDirection={isMobile ? 'column' : 'row'}>
      <Box alignItems="center" flex grow style={{ width: '100%' }}>
        <Avatar
          name={name}
          size={isMobile ? 'lg' : 'xl'}
          src={picture}
          color={getAvatarColor(application)}
        />
        <Box ml={isMobile ? 8 : 16}>
          <Stack space={2}>
            <Stack space={1}>
              <Text font="heading2" text={name} />
              <ApplicationMetaRow
                variant="neutral"
                label={t('application.header.applied')}
                detail={dt}
              />
              <ApplicationMetaRow
                variant={status}
                label={t(applicantStatusTranslationMap[status])}
                detail={statusDetail}
              />
            </Stack>
            <ApplicationLabels application={application} />
          </Stack>
        </Box>
      </Box>

      {hasRolePermission('application:view-options') && (
        <Box flex w="100%" justifyContent="flex-end" mt={isMobile ? 24 : 0}>
          <Stack
            orientation="horizontal"
            space={2}
            justify={isMobile ? 'flex-start' : 'flex-end'}
            style={{ width: isMobile ? '100%' : 'auto' }}
          >
            <Fragment>
              {!application.assignedTo ? (
                hasRolePermission('application:assign') && (
                  <IconButton
                    icon={<IconAssign />}
                    tooltip={t('application.tip.assign')}
                    rounded
                    onClick={hasRolePermission('application:assign') ? assign : null}
                  />
                )
              ) : (
                <Menu
                  buttonProps={{
                    variant: 'secondary',
                    style: { padding: '0' },
                    rounded: true,
                    iconLeft: (
                      <Tooltip
                        content={t('application.header.assignedto', application.assignedTo.name)}
                      >
                        <Avatar
                          src={application.assignedTo.picture}
                          name={application.assignedTo.name}
                          size="lg"
                        />
                      </Tooltip>
                    ),
                  }}
                >
                  <Menu.Item
                    onSelect={assign}
                    icon={<IconEdit />}
                    label={t('application.assignee.edit')}
                  />
                  <Menu.Item
                    onSelect={removeAssignee.mutate}
                    icon={<IconPersonRemove />}
                    label={t('application.assignee.remove')}
                  />
                </Menu>
              )}
            </Fragment>

            {application.isHired && (
              <Link to={application.onboardingUrl}>
                <IconButton icon={<IconOnboarding />} tooltip={t('application.gotoonboarding')} />
              </Link>
            )}
            {hasRolePermission('application:edit-stages') && (
              <StageMenu
                value={application.jobStageId}
                options={job.applicantJobStages}
                onSelect={changeStage}
                isFullWidth={isMobile}
              />
            )}
            <ApplicationOptions
              data={application}
              redirect={application.job.detailUrl}
              grayBg
              t={t}
            />
          </Stack>
        </Box>
      )}
    </Box>
  );
});
